import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { queryClient } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { 
  getUsers, createUser, updateUser, deleteUser, getOrganizationById,
  getGroups, createGroup, updateGroup, deleteGroup, setGroupMembers, getGroup
} from "../api/axios";
import { UserPlus, Users, SearchX, UserCog, Trash2, Edit } from "lucide-react";

// User type definition matching backend schema
type User = {
  _id: string;
  username: string;
  displayname: string;
  email: string;
  role: "admin" | "operator" | "viewer" | "superadmin";
  active: boolean;
  organization?: string;
  createdAt: string;
  updatedAt: string;
};

// Group type definition matching backend schema
type Group = {
  _id: string;
  name: string;
  organization: string;
  members: string[] | User[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
};

export default function UserManagement() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [searchTerm, setSearchTerm] = useState("");
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [showManageMembersDialog, setShowManageMembersDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [newUser, setNewUser] = useState({
    username: "",
    displayname: "", // Changed from displayName to match backend
    email: "",
    password: "",
    role: "viewer" as "admin" | "operator" | "viewer",
    active: true,
    organizationType: "join" as const, // Required by backend
    joinCode: "", // Will be populated from current user's org
  });
  const [newGroup, setNewGroup] = useState({
    name: "",
    memberIds: [] as string[],
  });

  // Fetch current user's organization info to get the join code
  const { data: organization } = useQuery({
    queryKey: ["organization", currentUser?.organization],
    queryFn: () => currentUser?.organization ? getOrganizationById(currentUser.organization) : null,
    enabled: !!currentUser?.organization,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Update joinCode when organization data is available
  useEffect(() => {
    if (organization?.joinCode) {
      setNewUser(prev => ({ ...prev, joinCode: organization.joinCode }));
    }
  }, [organization]);

  // Fetch users using real API
  const { data: users = [], isLoading, error } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: getUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  // Fetch groups using real API
  const { data: groups = [], isLoading: isLoadingGroups } = useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => getGroups({ withMembers: true }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    enabled: activeTab === "groups",
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: typeof newUser) => {
      // Ensure we have the join code before creating user
      if (!userData.joinCode && organization?.joinCode) {
        userData.joinCode = organization.joinCode;
      }
      
      return createUser(userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setShowAddUserDialog(false);
      setNewUser({
        username: "",
        displayname: "",
        email: "",
        password: "",
        role: "viewer",
        active: true,
        organizationType: "join",
        joinCode: organization?.joinCode || "",
      });
      toast({
        title: "User created",
        description: "The user has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to create user: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: Partial<User> }) =>
      updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "User updated",
        description: "The user has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update user: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "User deleted",
        description: "The user has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to delete user: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  // Group mutations - MUST be called before any early returns
  const createGroupMutation = useMutation({
    mutationFn: createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowAddGroupDialog(false);
      setNewGroup({ name: "", memberIds: [] });
      toast({
        title: "Group created",
        description: "The group has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to create group: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ groupId, groupData }: { groupId: string; groupData: { name?: string } }) =>
      updateGroup(groupId, groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowEditGroupDialog(false);
      setSelectedGroup(null);
      toast({
        title: "Group updated",
        description: "The group has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update group: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({
        title: "Group deleted",
        description: "The group has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to delete group: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const setGroupMembersMutation = useMutation({
    mutationFn: ({ groupId, memberIds }: { groupId: string; memberIds: string[] }) =>
      setGroupMembers(groupId, memberIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowManageMembersDialog(false);
      setSelectedGroup(null);
      toast({
        title: "Members updated",
        description: "Group members have been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update members: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  // Show error state - AFTER all hooks
  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">
            Failed to load users: {(error as any).response?.data?.message || (error as any).message}
          </div>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ["users"] })}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded w-1/6"></div>
        </div>
        <div className="h-12 bg-gray-200 rounded w-full"></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  // Apply search filter to real users data
  const filteredUsers = users.filter(
    (user) =>
      searchTerm === "" ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that we have organization join code
    if (!newUser.joinCode && !organization?.joinCode) {
      toast({
        title: "Error",
        description: "Organization information is required to create users. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }

    createUserMutation.mutate(newUser);
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleToggleUserStatus = (userId: string, currentStatus: boolean) => {
    updateUserMutation.mutate({
      userId,
      userData: { active: !currentStatus },
    });
  };

  const handleCreateGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroup.name.trim()) {
      toast({
        title: "Error",
        description: "Group name is required",
        variant: "destructive",
      });
      return;
    }
    createGroupMutation.mutate(newGroup);
  };

  const handleUpdateGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup || !newGroup.name.trim()) {
      toast({
        title: "Error",
        description: "Group name is required",
        variant: "destructive",
      });
      return;
    }
    updateGroupMutation.mutate({
      groupId: selectedGroup._id,
      groupData: { name: newGroup.name },
    });
  };

  const handleDeleteGroup = (groupId: string) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      deleteGroupMutation.mutate(groupId);
    }
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setNewGroup({ name: group.name, memberIds: [] });
    setShowEditGroupDialog(true);
  };

  const handleManageMembers = async (group: Group) => {
    setSelectedGroup(group);
    // Fetch full group details with members
    try {
      const fullGroup = await getGroup(group._id, { withMembers: true });
      const memberIds = Array.isArray(fullGroup.members) 
        ? fullGroup.members.map((m: any) => m._id || m.id || m).filter(Boolean)
        : [];
      setNewGroup({ name: fullGroup.name, memberIds });
      setShowManageMembersDialog(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load group members",
        variant: "destructive",
      });
    }
  };

  const handleSaveMembers = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;
    setGroupMembersMutation.mutate({
      groupId: selectedGroup._id,
      memberIds: newGroup.memberIds,
    });
  };

  const toggleMemberSelection = (userId: string) => {
    setNewGroup(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(userId)
        ? prev.memberIds.filter(id => id !== userId)
        : [...prev.memberIds, userId],
    }));
  };

  // Apply search filter to groups
  const filteredGroups = groups.filter(
    (group) =>
      groupSearchTerm === "" ||
      group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">
              User & Group Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage user accounts, groups, and permissions
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="groups">
            <UserCog className="h-4 w-4 mr-2" />
            Groups
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex justify-end">
            {(currentUser?.role === "admin" || currentUser?.role === "superadmin") ? (
              <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>
                    Create a new user account with appropriate permissions.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="username" className="text-right">
                        Username
                      </Label>
                      <Input
                        id="username"
                        value={newUser.username}
                        onChange={(e) =>
                          setNewUser({ ...newUser, username: e.target.value })
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="displayName" className="text-right">
                        Display Name
                      </Label>
                      <Input
                        id="displayName"
                        value={newUser.displayname}
                        onChange={(e) =>
                          setNewUser({ ...newUser, displayname: e.target.value })
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="email" className="text-right">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) =>
                          setNewUser({ ...newUser, email: e.target.value })
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="password" className="text-right">
                        Password
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) =>
                          setNewUser({ ...newUser, password: e.target.value })
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="role" className="text-right">
                        Role
                      </Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value: "admin" | "operator" | "viewer") =>
                          setNewUser({ ...newUser, role: value })
                        }
                      >
                        <SelectTrigger className="col-span-3">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrator</SelectItem>
                          <SelectItem value="operator">Operator</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="active" className="text-right">
                        Active
                      </Label>
                      <div className="col-span-3 flex items-center">
                        <Switch
                          id="active"
                          checked={newUser.active}
                          onCheckedChange={(checked) =>
                            setNewUser({ ...newUser, active: checked })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={createUserMutation.isPending}
                    >
                      {createUserMutation.isPending ? "Creating..." : "Create User"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            ) : (
              <div className="text-sm text-gray-500">
                Only administrators can add new users
              </div>
            )}
          </div>

          {/* Search */}
          <div className="mb-6">
            <Input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-gray-500">
              {users.length === 0 ? (
                <>
                  <Users className="h-12 w-12 mb-4 block text-gray-400" />
                  <h3 className="text-lg font-medium mb-2">No users found</h3>
                  <p className="text-sm">
                    {currentUser?.role === "admin" || currentUser?.role === "superadmin" 
                      ? "Get started by adding your first user." 
                      : "No users are currently available."}
                  </p>
                </>
              ) : (
                <>
                  <SearchX className="h-12 w-12 mb-4 block text-gray-400" />
                  <h3 className="text-lg font-medium mb-2">No users match your search</h3>
                  <p className="text-sm">Try adjusting your search terms</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                          <span className="text-primary font-medium text-sm">
                            {user.displayname
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")
                              .toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.displayname}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{user.username} • {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === "admin" || user.role === "superadmin"
                            ? "bg-purple-100 text-purple-800"
                            : user.role === "operator"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {user.role === "admin"
                          ? "Administrator"
                          : user.role === "superadmin"
                          ? "Super Admin"
                          : user.role === "operator"
                          ? "Operator"
                          : "Viewer"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.active
                            ? "bg-secondary bg-opacity-10 text-secondary"
                            : "bg-danger bg-opacity-10 text-danger"
                        }`}
                      >
                        {user.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleToggleUserStatus(user._id, user.active)
                          }
                          disabled={updateUserMutation.isPending}
                        >
                          {user.active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-primary"
                          disabled={currentUser?.role !== "admin" && currentUser?.role !== "superadmin"}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger hover:text-danger hover:bg-danger hover:bg-opacity-10"
                          onClick={() => handleDeleteUser(user._id)}
                          disabled={deleteUserMutation.isPending || (currentUser?.role !== "admin" && currentUser?.role !== "superadmin")}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Search groups..."
                value={groupSearchTerm}
                onChange={(e) => setGroupSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            {(currentUser?.role === "admin" || currentUser?.role === "superadmin") && (
              <Dialog open={showAddGroupDialog} onOpenChange={setShowAddGroupDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Group
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Group</DialogTitle>
                    <DialogDescription>
                      Create a new group and optionally add members.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateGroup}>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="groupName">Group Name</Label>
                        <Input
                          id="groupName"
                          value={newGroup.name}
                          onChange={(e) =>
                            setNewGroup({ ...newGroup, name: e.target.value })
                          }
                          placeholder="e.g., Network Administrators"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Select Members (Optional)</Label>
                        <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
                          {users.length === 0 ? (
                            <p className="text-sm text-gray-500">No users available</p>
                          ) : (
                            <div className="space-y-2">
                              {users.map((user) => (
                                <div key={user._id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`member-${user._id}`}
                                    checked={newGroup.memberIds.includes(user._id)}
                                    onCheckedChange={() => toggleMemberSelection(user._id)}
                                  />
                                  <Label
                                    htmlFor={`member-${user._id}`}
                                    className="text-sm font-normal cursor-pointer flex-1"
                                  >
                                    {user.displayname} ({user.username})
                                  </Label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddGroupDialog(false);
                          setNewGroup({ name: "", memberIds: [] });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createGroupMutation.isPending}>
                        {createGroupMutation.isPending ? "Creating..." : "Create Group"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Groups Table */}
          {isLoadingGroups ? (
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          ) : filteredGroups.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="text-gray-500">
                  {groups.length === 0 ? (
                    <>
                      <UserCog className="h-12 w-12 mb-4 block text-gray-400 mx-auto" />
                      <h3 className="text-lg font-medium mb-2">No groups found</h3>
                      <p className="text-sm">
                        {currentUser?.role === "admin" || currentUser?.role === "superadmin"
                          ? "Get started by creating your first group."
                          : "No groups are currently available."}
                      </p>
                    </>
                  ) : (
                    <>
                      <SearchX className="h-12 w-12 mb-4 block text-gray-400 mx-auto" />
                      <h3 className="text-lg font-medium mb-2">No groups match your search</h3>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Members
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredGroups.map((group) => {
                        const memberCount = Array.isArray(group.members)
                          ? group.members.length
                          : 0;
                        const memberList = Array.isArray(group.members) && group.members.length > 0
                          ? group.members
                          : [];
                        
                        return (
                          <tr key={group._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {group.name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <Badge variant="secondary">{memberCount} members</Badge>
                                {memberList.length > 0 && memberList.length <= 3 && (
                                  <div className="text-xs text-gray-500">
                                    {memberList.map((m: any, idx: number) => {
                                      const member = typeof m === 'object' ? m : users.find((u: User) => u._id === m);
                                      return member ? (
                                        <span key={idx}>
                                          {member.displayname || member.username}
                                          {idx < memberList.length - 1 ? ", " : ""}
                                        </span>
                                      ) : null;
                                    })}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(group.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleManageMembers(group)}
                                  disabled={currentUser?.role !== "admin" && currentUser?.role !== "superadmin"}
                                >
                                  <UserCog className="h-4 w-4 mr-1" />
                                  Members
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditGroup(group)}
                                  disabled={currentUser?.role !== "admin" && currentUser?.role !== "superadmin"}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-danger hover:text-danger hover:bg-danger hover:bg-opacity-10"
                                  onClick={() => handleDeleteGroup(group._id)}
                                  disabled={deleteGroupMutation.isPending || (currentUser?.role !== "admin" && currentUser?.role !== "superadmin")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Edit Group Dialog */}
          <Dialog open={showEditGroupDialog} onOpenChange={setShowEditGroupDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Group</DialogTitle>
                <DialogDescription>
                  Update the group name.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateGroup}>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="editGroupName">Group Name</Label>
                    <Input
                      id="editGroupName"
                      value={newGroup.name}
                      onChange={(e) =>
                        setNewGroup({ ...newGroup, name: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditGroupDialog(false);
                      setSelectedGroup(null);
                      setNewGroup({ name: "", memberIds: [] });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateGroupMutation.isPending}>
                    {updateGroupMutation.isPending ? "Updating..." : "Update Group"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Manage Members Dialog */}
          <Dialog open={showManageMembersDialog} onOpenChange={setShowManageMembersDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Manage Group Members</DialogTitle>
                <DialogDescription>
                  Select users to add or remove from {selectedGroup?.name}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSaveMembers}>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Members</Label>
                    <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
                      {users.length === 0 ? (
                        <p className="text-sm text-gray-500">No users available</p>
                      ) : (
                        <div className="space-y-2">
                          {users.map((user) => (
                            <div key={user._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`manage-member-${user._id}`}
                                checked={newGroup.memberIds.includes(user._id)}
                                onCheckedChange={() => toggleMemberSelection(user._id)}
                              />
                              <Label
                                htmlFor={`manage-member-${user._id}`}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-medium">{user.displayname}</div>
                                    <div className="text-xs text-gray-500">
                                      @{user.username} • {user.email}
                                    </div>
                                  </div>
                                  <Badge
                                    variant={
                                      user.role === "admin" || user.role === "superadmin"
                                        ? "default"
                                        : user.role === "operator"
                                        ? "secondary"
                                        : "outline"
                                    }
                                  >
                                    {user.role}
                                  </Badge>
                                </div>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {newGroup.memberIds.length} member(s) selected
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowManageMembersDialog(false);
                      setSelectedGroup(null);
                      setNewGroup({ name: "", memberIds: [] });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={setGroupMembersMutation.isPending}>
                    {setGroupMembersMutation.isPending ? "Saving..." : "Save Members"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
